// hooks/useAlbumTracks.ts
import { useEffect, useState } from "react";
import SpotifyWebApi from "spotify-web-api-js";
import { useTokenContext } from "src/auth/UserTokenProvider";

const spotifyApi = new SpotifyWebApi();

export interface AlbumTrack extends SpotifyApi.TrackObjectSimplified {
  favorite?: boolean;
  popularity?: number;
}

const SPOTIFY_BATCH_LIMIT = 50;

const chunk = <T>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const fetchAllAlbumTracks = async (
  albumId: string
): Promise<SpotifyApi.TrackObjectSimplified[]> => {
  const items: SpotifyApi.TrackObjectSimplified[] = [];
  let offset = 0;
  let total = 0;

  do {
    const response = await spotifyApi.getAlbumTracks(albumId, {
      limit: SPOTIFY_BATCH_LIMIT,
      offset
    });
    items.push(...response.items);
    total = response.total;
    offset += response.items.length;
  } while (offset < total);

  return items;
};

const useAlbumTracks = (albumId?: string) => {
  const { token, isLoading: tokenLoading } = useTokenContext();
  const [tracks, setTracks] = useState<AlbumTrack[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAlbumTracks = async () => {
      if (!albumId) {
        setTracks([]);
        return;
      }
      if (!token?.value) {
        setTracks([]);
        setLoading(tokenLoading);
        setError(tokenLoading ? null : "Spotify authentication is required");
        return;
      }
      try {
        spotifyApi.setAccessToken(token.value);
        setLoading(true);
        setError(null);

        const tracks = await fetchAllAlbumTracks(albumId);
        const tracksWithFavorite: AlbumTrack[] = [...tracks];
        const trackIds = tracks.map(track => track.id);

        const likedTracks = (
          await Promise.all(
            chunk(trackIds, SPOTIFY_BATCH_LIMIT).map(ids => spotifyApi.containsMySavedTracks(ids))
          )
        ).flat();
        const fullTracks = (
          await Promise.all(
            chunk(trackIds, SPOTIFY_BATCH_LIMIT).map(ids => spotifyApi.getTracks(ids))
          )
        ).flatMap(result => result.tracks);

        const popularityById = new Map(fullTracks.map(track => [track.id, track.popularity]));

        tracksWithFavorite.forEach((track, index) => {
          track.popularity = popularityById.get(track.id);
          track.favorite = likedTracks[index];
        });
        setTracks(tracksWithFavorite);
      } catch (err) {
        setError("Failed to fetch album tracks");
      } finally {
        setLoading(false);
      }
    };

    fetchAlbumTracks();
  }, [albumId, token?.value, tokenLoading]);

  return { tracks, loading, error, setError };
};

export default useAlbumTracks;
